import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, User, Phone, Mail, Building2, Tag,
  ExternalLink, Pencil, Briefcase,
  Linkedin, Hash, StickyNote, ChevronDown, ChevronUp,
  Smartphone, Star, Globe,
  AlertCircle, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useContactDetail } from '../hooks/useCrmContactDetail';
import { useUpdateContact } from '../hooks/useCrmContacts';
import { ContactAccountRolesCard } from '../components/ContactAccountRolesCard';
import { CrmNoteFeed } from '../components/notes/CrmNoteFeed';
import type { Contact, CreateContactInput } from '../types';

// =============================================================================
// Constants
// =============================================================================

const SOURCE_COLORS: Record<string, string> = {
  hubspot: 'bg-orange-100 text-orange-800',
  apollo: 'bg-purple-100 text-purple-800',
  linkedin: 'bg-blue-100 text-blue-800',
  manual: 'bg-gray-100 text-gray-800',
};

const STATUS_COLORS: Record<string, string> = {
  prospect: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-600',
  archived: 'bg-red-100 text-red-800',
};

// =============================================================================
// InfoRow helper
// =============================================================================

function InfoRow({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-border/50 last:border-0">
      {icon && <span className="mt-0.5 text-muted-foreground">{icon}</span>}
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm break-words">{value}</div>
      </div>
    </div>
  );
}

// =============================================================================
// ContactInfoSidebar
// =============================================================================

function ContactInfoSidebar({ contact, onEdit }: { contact: Contact; onEdit: () => void }) {
  const { t } = useTranslation('crm');
  const [notesExpanded, setNotesExpanded] = useState(false);
  const NOTES_LIMIT = 150;
  const hasLongNotes = (contact.notes?.length ?? 0) > NOTES_LIMIT;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{t('contacts.info.cardTitle')}</CardTitle>
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5 mr-1" /> {t('common.edit')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-0">
        <InfoRow
          label={t('contacts.info.company')}
          icon={<Building2 className="h-3.5 w-3.5" />}
          value={
            contact.company_id ? (
              <Link
                to={`/crm/companies/${contact.company_id}`}
                className="text-primary hover:underline flex items-center gap-1"
              >
                {contact.company_name || 'View Company'} <ExternalLink className="h-3 w-3" />
              </Link>
            ) : null
          }
        />
        <InfoRow label={t('contacts.info.title')} icon={<Briefcase className="h-3.5 w-3.5" />} value={contact.title} />
        <InfoRow label={t('contacts.info.department')} value={contact.department} />
        <InfoRow
          label={t('contacts.info.email')}
          icon={<Mail className="h-3.5 w-3.5" />}
          value={
            contact.email ? (
              <a href={`mailto:${contact.email}`} className="text-primary hover:underline">
                {contact.email}
              </a>
            ) : null
          }
        />
        <InfoRow
          label={t('contacts.info.phone')}
          icon={<Phone className="h-3.5 w-3.5" />}
          value={contact.phone}
        />
        <InfoRow
          label={t('contacts.info.mobile')}
          icon={<Smartphone className="h-3.5 w-3.5" />}
          value={contact.mobile_phone}
        />
        <InfoRow
          label={t('contacts.info.source')}
          icon={<Globe className="h-3.5 w-3.5" />}
          value={
            contact.source ? (
              <Badge variant="outline" className={SOURCE_COLORS[contact.source] || 'bg-gray-100 text-gray-800'}>
                {contact.source}
              </Badge>
            ) : null
          }
        />
        <InfoRow
          label={t('contacts.info.status')}
          icon={<Hash className="h-3.5 w-3.5" />}
          value={
            contact.status ? (
              <Badge variant="outline" className={STATUS_COLORS[contact.status] || 'bg-gray-100 text-gray-800'}>
                {contact.status}
              </Badge>
            ) : null
          }
        />
        <InfoRow
          label={t('contacts.info.linkedin')}
          icon={<Linkedin className="h-3.5 w-3.5" />}
          value={
            contact.linkedin_url ? (
              <a
                href={contact.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline flex items-center gap-1"
              >
                {contact.linkedin_url.replace(/^https?:\/\/(www\.)?linkedin\.com\//, '')}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : null
          }
        />
        {contact.linkedin_headline && (
          <InfoRow label={t('contacts.info.linkedinHeadline')} value={contact.linkedin_headline} />
        )}
        {contact.hubspot_contact_id && (
          <InfoRow label={t('contacts.info.hubspotId')} value={<span className="text-xs font-mono">{contact.hubspot_contact_id}</span>} />
        )}
        {contact.apollo_contact_id && (
          <InfoRow label={t('contacts.info.apolloId')} value={<span className="text-xs font-mono">{contact.apollo_contact_id}</span>} />
        )}
        {contact.tags && contact.tags.length > 0 && (
          <InfoRow
            label={t('contacts.info.tags')}
            icon={<Tag className="h-3.5 w-3.5" />}
            value={
              <div className="flex flex-wrap gap-1 mt-0.5">
                {contact.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                ))}
              </div>
            }
          />
        )}
        {contact.notes && (
          <InfoRow
            label={t('contacts.info.notes')}
            icon={<StickyNote className="h-3.5 w-3.5" />}
            value={
              <div>
                <p className="whitespace-pre-wrap text-sm">
                  {notesExpanded || !hasLongNotes
                    ? contact.notes
                    : `${contact.notes.slice(0, NOTES_LIMIT)}...`}
                </p>
                {hasLongNotes && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 h-auto text-xs"
                    onClick={() => setNotesExpanded(!notesExpanded)}
                  >
                    {notesExpanded ? (
                      <>{t('common.showLess')} <ChevronUp className="h-3 w-3 ml-0.5" /></>
                    ) : (
                      <>{t('common.showMore')} <ChevronDown className="h-3 w-3 ml-0.5" /></>
                    )}
                  </Button>
                )}
              </div>
            }
          />
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// CompanyCard (right column)
// =============================================================================

function CompanyCard({ contact }: { contact: Contact }) {
  const { t } = useTranslation('crm');
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t('contacts.company.cardTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        {contact.company_id ? (
          <Link
            to={`/crm/companies/${contact.company_id}`}
            className="flex items-center gap-2 p-2 rounded border bg-muted/30 text-sm text-primary hover:underline font-medium"
          >
            <Building2 className="h-4 w-4" />
            {contact.company_name || t('contacts.company.viewCompany')}
          </Link>
        ) : (
          <div className="text-center py-4 text-muted-foreground">
            <Building2 className="h-6 w-6 mx-auto mb-1.5 opacity-40" />
            <p className="text-sm">{t('contacts.company.noCompany')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// LinkedInProfileCard (right column)
// =============================================================================

function LinkedInProfileCard({ contact }: { contact: Contact }) {
  const { t } = useTranslation('crm');
  const hasLinkedIn = contact.linkedin_url || contact.linkedin_headline || contact.linkedin_about || contact.linkedin_avatar_url;
  if (!hasLinkedIn) return null;

  const [aboutExpanded, setAboutExpanded] = useState(false);
  const ABOUT_LIMIT = 200;
  const hasLongAbout = (contact.linkedin_about?.length ?? 0) > ABOUT_LIMIT;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-1.5">
          <Linkedin className="h-4 w-4" /> {t('contacts.linkedin.cardTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {contact.linkedin_avatar_url && (
          <div className="flex justify-center">
            <img
              src={contact.linkedin_avatar_url}
              alt={`${contact.first_name} ${contact.last_name}`}
              className="h-16 w-16 rounded-full object-cover"
            />
          </div>
        )}
        {contact.linkedin_headline && (
          <p className="text-sm text-muted-foreground text-center">{contact.linkedin_headline}</p>
        )}
        {contact.linkedin_about && (
          <div>
            <p className="text-xs text-muted-foreground font-medium mb-1">{t('contacts.linkedin.about')}</p>
            <p className="text-sm whitespace-pre-wrap">
              {aboutExpanded || !hasLongAbout
                ? contact.linkedin_about
                : `${contact.linkedin_about.slice(0, ABOUT_LIMIT)}...`}
            </p>
            {hasLongAbout && (
              <Button
                variant="link"
                size="sm"
                className="p-0 h-auto text-xs"
                onClick={() => setAboutExpanded(!aboutExpanded)}
              >
                {aboutExpanded ? (
                  <>{t('common.showLess')} <ChevronUp className="h-3 w-3 ml-0.5" /></>
                ) : (
                  <>{t('common.showMore')} <ChevronDown className="h-3 w-3 ml-0.5" /></>
                )}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// ExternalLinksCard (right column)
// =============================================================================

function ExternalLinksCard({ contact }: { contact: Contact }) {
  const { t } = useTranslation('crm');
  const hasLinks = contact.hubspot_contact_id || contact.linkedin_url || contact.email;
  if (!hasLinks) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t('contacts.externalLinks.cardTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-1.5">
          {contact.hubspot_contact_id && (
            <a
              href={`https://app.hubspot.com/contacts/22484617/record/0-1/${contact.hubspot_contact_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" /> HubSpot
            </a>
          )}
          {contact.linkedin_url && (
            <a
              href={contact.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <Linkedin className="h-3.5 w-3.5" /> LinkedIn
            </a>
          )}
          {contact.email && (
            <a
              href={`mailto:${contact.email}`}
              className="flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <Mail className="h-3.5 w-3.5" /> {contact.email}
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// ContactModal (edit)
// =============================================================================

function ContactModal({
  open,
  onOpenChange,
  contact,
  onSave,
  isSaving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: Contact | null;
  onSave: (input: CreateContactInput) => Promise<void>;
  isSaving: boolean;
}) {
  const { t } = useTranslation('crm');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [title, setTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open && contact) {
      setFirstName(contact.first_name);
      setLastName(contact.last_name);
      setEmail(contact.email || '');
      setPhone(contact.phone || '');
      setMobilePhone(contact.mobile_phone || '');
      setTitle(contact.title || '');
      setDepartment(contact.department || '');
      setNotes(contact.notes || '');
    } else if (open && !contact) {
      setFirstName(''); setLastName(''); setEmail(''); setPhone('');
      setMobilePhone(''); setTitle(''); setDepartment(''); setNotes('');
    }
  }, [open, contact]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave({
      firstName,
      lastName,
      email: email || undefined,
      phone: phone || undefined,
      mobilePhone: mobilePhone || undefined,
      title: title || undefined,
      department: department || undefined,
      notes: notes || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('contacts.modal.titleEdit')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('contacts.modal.firstName')}</Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </div>
            <div>
              <Label>{t('contacts.modal.lastName')}</Label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </div>
            <div>
              <Label>{t('contacts.modal.email')}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label>{t('contacts.modal.phone')}</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div>
              <Label>{t('contacts.modal.mobile')}</Label>
              <Input value={mobilePhone} onChange={(e) => setMobilePhone(e.target.value)} />
            </div>
            <div>
              <Label>{t('contacts.modal.title')}</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label>{t('contacts.modal.department')}</Label>
              <Input value={department} onChange={(e) => setDepartment(e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label>{t('contacts.modal.notes')}</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={!firstName.trim() || !lastName.trim() || isSaving}>
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Loading Skeleton
// =============================================================================

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-5 w-16" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        <div className="lg:col-span-1"><Skeleton className="h-[400px]" /></div>
        <div className="lg:col-span-2"><Skeleton className="h-[400px]" /></div>
        <div className="lg:col-span-1 space-y-4">
          <Skeleton className="h-[200px]" />
          <Skeleton className="h-[200px]" />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Not Found
// =============================================================================

function NotFound() {
  const { t } = useTranslation('crm');
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
      <User className="h-12 w-12 text-muted-foreground opacity-40" />
      <h2 className="text-lg font-semibold">{t('contacts.notFound')}</h2>
      <Button variant="outline" onClick={() => navigate('/crm/contacts')}>
        <ArrowLeft className="h-4 w-4 mr-1" /> {t('contacts.notFoundBack')}
      </Button>
    </div>
  );
}

// =============================================================================
// Fetch Error (non-404)
// =============================================================================

function FetchError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('crm');
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 p-4">
      <Alert variant="destructive" className="max-w-md">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t('contacts.failedToLoadDetail')}</AlertTitle>
        <AlertDescription className="mt-1">
          {t('common.somethingWentWrongRetry')}
        </AlertDescription>
      </Alert>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onRetry}>
          <RefreshCw className="h-4 w-4 mr-1" /> {t('common.retry')}
        </Button>
        <Button variant="ghost" onClick={() => navigate('/crm/contacts')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> {t('contacts.notFoundBack')}
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// CrmContactDetailPage
// =============================================================================

export function CrmContactDetailPage() {
  const { t } = useTranslation('crm');
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const { data: contact, isLoading, isError, error, refetch } = useContactDetail(contactId!);
  const updateMutation = useUpdateContact();

  const [showEditModal, setShowEditModal] = useState(false);

  const handleSave = useCallback(async (input: CreateContactInput) => {
    if (!contactId) return;
    await updateMutation.mutateAsync({ id: contactId, ...input });
    setShowEditModal(false);
  }, [contactId, updateMutation]);

  if (isLoading) return <LoadingSkeleton />;
  if (isError) {
    const status = (error as { status?: number } | null)?.status;
    if (status === 404) return <NotFound />;
    return <FetchError onRetry={() => refetch()} />;
  }
  if (!contact) return <NotFound />;

  const fullName = `${contact.first_name} ${contact.last_name}`;

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/crm/contacts')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> {t('contacts.pageTitle')}
        </Button>
        <h1 className="text-xl font-semibold">{fullName}</h1>
        {contact.is_primary && (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 flex items-center gap-1">
            <Star className="h-3 w-3" /> {t('common.primaryBadge')}
          </Badge>
        )}
        {contact.source && (
          <Badge variant="outline" className={SOURCE_COLORS[contact.source] || 'bg-gray-100 text-gray-800'}>
            {contact.source}
          </Badge>
        )}
      </div>

      {/* 3-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        {/* LEFT: Contact Info */}
        <div className="lg:col-span-1">
          <ContactInfoSidebar contact={contact} onEdit={() => setShowEditModal(true)} />
        </div>

        {/* CENTER: Activity Feed */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('contacts.notes')}</CardTitle>
            </CardHeader>
            <CardContent>
              <CrmNoteFeed entityType="contact" entityId={contactId!} />
            </CardContent>
          </Card>
        </div>

        {/* RIGHT: Account Roles + Company + LinkedIn + External Links */}
        <div className="lg:col-span-1 space-y-4">
          <ContactAccountRolesCard
            contactId={contact.id}
            companyAccountId={contact.company_account_id ?? null}
          />
          <CompanyCard contact={contact} />
          <LinkedInProfileCard contact={contact} />
          <ExternalLinksCard contact={contact} />
        </div>
      </div>

      {/* Edit Modal */}
      <ContactModal
        open={showEditModal}
        onOpenChange={(v) => setShowEditModal(v)}
        contact={contact}
        onSave={handleSave}
        isSaving={updateMutation.isPending}
      />
    </div>
  );
}
